import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  PencilSquareIcon,
  PhotoIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import SignalCreationForm from './SignalCreationForm';
import SmartSignalProcessor from './SmartSignalProcessor';
import SignalImport from './SignalImport';

interface SignalCreationHubProps {
  isOpen: boolean;
  onClose: () => void;
  onSignalCreated?: (response: any) => void;
  onImportComplete?: (sessionId: string, signalCount: number) => void;
}

type CreationMode = 'selection' | 'manual' | 'image' | 'csv';

const SignalCreationHub: React.FC<SignalCreationHubProps> = ({
  isOpen,
  onClose,
  onSignalCreated,
  onImportComplete
}) => {
  const [mode, setMode] = useState<CreationMode>('selection');
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    setMode('selection');
    onClose();
  }, [onClose]);

  const handleBackToSelection = useCallback(() => {
    setMode('selection');
  }, []);

  const handleManualSubmit = useCallback((signalData: any) => {
    console.log('Creating manual signal:', signalData);
    onSignalCreated?.(signalData);
    handleClose();
  }, [onSignalCreated, handleClose]);

  const handleSmartProcessorSubmit = useCallback((response: any) => {
    console.log('Smart processor signal created:', response);
    onSignalCreated?.(response);
    handleClose();
  }, [onSignalCreated, handleClose]);

  const handleImportComplete = useCallback((sessionId: string, signalCount: number) => {
    console.log(`Import complete: ${signalCount} signals imported from session ${sessionId}`);
    onImportComplete?.(sessionId, signalCount);
    handleClose();
  }, [onImportComplete, handleClose]);

  const handleAddConnector = useCallback(() => {
    handleClose();
    navigate('/v3/settings/connectors');
  }, [handleClose, navigate]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative"
          >
            {/* Selection View */}
            {mode === 'selection' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-8"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Add Signals</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Choose how you want to create or import signals
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Options Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Manual Entry Option */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setMode('manual')}
                    className="group relative bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 text-left hover:border-blue-400 hover:shadow-lg transition-all"
                  >
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <PencilSquareIcon className="w-8 h-8 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Manual Entry</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Create a single signal manually
                        </p>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Quick
                      </span>
                    </div>
                  </motion.button>

                  {/* Image/Document Option */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setMode('image')}
                    className="group relative bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 text-left hover:border-purple-400 hover:shadow-lg transition-all"
                  >
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                        <PhotoIcon className="w-8 h-8 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Smart Extract</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Extract from image or document
                        </p>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        AI
                      </span>
                    </div>
                  </motion.button>

                  {/* CSV Import Option */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setMode('csv')}
                    className="group relative bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 text-left hover:border-green-400 hover:shadow-lg transition-all"
                  >
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <DocumentTextIcon className="w-8 h-8 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Bulk Import</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Import multiple signals from CSV
                        </p>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Batch
                      </span>
                    </div>
                  </motion.button>

                  {/* Add Connector Option */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAddConnector}
                    className="group relative bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-6 text-left hover:border-orange-400 hover:shadow-lg transition-all"
                  >
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                        <LinkIcon className="w-8 h-8 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Add Connector</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Set up automated data sources
                        </p>
                      </div>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Auto
                      </span>
                    </div>
                  </motion.button>
                </div>

                {/* Footer with tips */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-start space-x-2">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Tip:</span> Use Smart Extract for screenshots, emails, or documents. 
                      CSV Import is best for large batches of signals from spreadsheets or exported data.
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Manual Entry Form */}
            {mode === 'manual' && (
              <div className="relative">
                <SignalCreationForm
                  isOpen={true}
                  onClose={handleClose}
                  onSubmit={handleManualSubmit}
                  onBack={handleBackToSelection}
                />
              </div>
            )}

            {/* Smart Processor (Image/Document) */}
            {mode === 'image' && (
              <div className="relative">
                <SmartSignalProcessor
                  isOpen={true}
                  onClose={handleClose}
                  onSubmit={handleSmartProcessorSubmit}
                  onBack={handleBackToSelection}
                />
              </div>
            )}

            {/* CSV Import with Auto-start */}
            {mode === 'csv' && (
              <div className="relative">
                <SignalImport
                  isOpen={true}
                  onClose={handleClose}
                  onImportComplete={handleImportComplete}
                />
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SignalCreationHub;